/*=========================================================================================
  File Name: a11yDom.js
  Description: DOM-level accessibility repairs for markup this app does not own.

               vuesax renders several widgets without the semantics WCAG 2.2 needs, and
               the app marks quiz answers right/wrong with a CSS class alone. Rather than
               fork vuesax or touch every activity, a single MutationObserver watches the
               document and repairs each pattern as it appears:

               - Font-icon ligature text ("check", "close", "keyboard_arrow_down") is
                 announced by screen readers: hide the <i> (audit issue 8).
               - <vs-collapse-item> headers are clickable <header>s: give them a button
                 role, a tab stop, aria-expanded and Enter/Space handling (audit 26.3/26.4).
               - $vs.dialog() renders `.vs-dialog` with no role, no name, no focus
                 management and a non-focusable close icon (audit 26.2, 34.2, 34.3).
               - <vs-list-header> / <vs-list-item> titles are visually headings but are
                 plain <div>s (audit 11.2, 11.4).
               - `.wrong` / `.correct` answer marking is colour-only: add an icon with a
                 text alternative and aria-invalid on the control (audit 17.1, 17.2).
               - Bare <select>/<textarea>/<input>s after a question paragraph have no
                 accessible name: use the question text (audit 13.1).

               Everything here is idempotent, so running it again over the same subtree is
               harmless. Nothing depends on vuesax private API - only on rendered classes.
==========================================================================================*/

import { FOCUSABLE } from '@/mixins/dialog';

const isVisible = (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed';

const focusablesIn = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => isVisible(el) && !el.hasAttribute('data-focus-sentinel'),
  );

const activateOnEnterOrSpace = (event) => {
  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault();
    event.currentTarget.click();
  }
};

// Matches `el` itself as well as its descendants.
function selectAll(root, selector) {
  const list = root.querySelectorAll ? Array.from(root.querySelectorAll(selector)) : [];
  if (root.matches && root.matches(selector)) list.unshift(root);
  return list;
}

// ---------------------------------------------------------------------------------------
// 1. Font icons (audit issue 8)
// ---------------------------------------------------------------------------------------

const ICON = 'i.vs-icon, i.material-icons, .vs-icon.material-icons';

function hideFontIcons(root) {
  selectAll(root, ICON).forEach((icon) => {
    // Icons that were promoted to controls (e.g. the vs-dialog close icon) carry their own
    // role and name; hiding them would remove them from the accessibility tree.
    if (icon.hasAttribute('role') || icon.hasAttribute('aria-label')) return;
    if (icon.getAttribute('aria-hidden') !== 'true') icon.setAttribute('aria-hidden', 'true');

    // An icon-only control loses its (garbage, but non-empty) name once the ligature text
    // is hidden. Derive a readable one from the ligature so the control is still named.
    const control = icon.closest('button, a, [role="button"]');
    if (
      control &&
      !control.hasAttribute('aria-label') &&
      !control.hasAttribute('aria-labelledby') &&
      !control.getAttribute('title')
    ) {
      const visibleText = Array.from(control.childNodes)
        .filter((n) => !(n.nodeType === 1 && n.getAttribute('aria-hidden') === 'true'))
        .map((n) => n.textContent || '')
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
      const ligature = (icon.textContent || '').trim();
      if (!visibleText && ligature) {
        const label = ligature.replace(/[_-]+/g, ' ');
        control.setAttribute('aria-label', label.charAt(0).toUpperCase() + label.slice(1));
      }
    }
  });
}

// ---------------------------------------------------------------------------------------
// 2. vs-collapse-item (audit 26.3, 26.4)
// ---------------------------------------------------------------------------------------

function enhanceCollapse(root) {
  selectAll(root, '.vs-collapse-item--header').forEach((header) => {
    const item = header.closest('.vs-collapse-item');
    if (!item) return;
    if (header.dataset.a11yCollapse !== 'true') {
      header.dataset.a11yCollapse = 'true';
      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      header.addEventListener('keydown', activateOnEnterOrSpace);
    }
    const expanded = item.classList.contains('open-item') ? 'true' : 'false';
    if (header.getAttribute('aria-expanded') !== expanded) {
      header.setAttribute('aria-expanded', expanded);
    }
  });
}

// ---------------------------------------------------------------------------------------
// 3. $vs.dialog (audit 26.2, 34.2, 34.3)
// ---------------------------------------------------------------------------------------

let dialogSeq = 0;
// Only one $vs.dialog is ever open at a time in this app, so a single slot is enough.
let openDialog = null;

function dialogClose(root) {
  const cancel = root.querySelector('.vs-dialog-cancel, .vs-dialog-cancel-button');
  if (cancel) cancel.click();
}

function trapDialogKeys(event) {
  const root = openDialog && openDialog.root;
  if (!root || !document.contains(root)) return;

  if (event.key === 'Escape' || event.key === 'Esc') {
    event.preventDefault();
    event.stopPropagation();
    dialogClose(root);
    return;
  }
  if (event.key !== 'Tab') return;

  const targets = focusablesIn(root);
  if (!targets.length) {
    event.preventDefault();
    return;
  }
  const first = targets[0];
  const last = targets[targets.length - 1];
  const active = document.activeElement;

  if (!root.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function enhanceDialogs(root) {
  selectAll(root, '.vs-dialog').forEach((dialog) => {
    if (dialog.dataset.a11yDialog === 'true') return;
    dialog.dataset.a11yDialog = 'true';

    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');

    const title = dialog.querySelector('.dialog-title');
    if (title) {
      if (!title.id) {
        dialogSeq += 1;
        title.id = `vs-dialog-title-${dialogSeq}`;
      }
      dialog.setAttribute('aria-labelledby', title.id);
    } else {
      dialog.setAttribute('aria-label', 'Dialog');
    }

    const text = dialog.querySelector('.vs-dialog-text');
    if (text) {
      if (!text.id) text.id = `${title ? title.id : `vs-dialog-${dialogSeq}`}-desc`;
      dialog.setAttribute('aria-describedby', text.id);
    }

    // The close "X" is a vs-icon <i>: not focusable, no name, no key handling.
    const close = dialog.querySelector('.vs-dialog-cancel');
    if (close) {
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
      close.setAttribute(
        'aria-label',
        title ? `Close ${title.textContent.trim()}` : 'Close dialog',
      );
      close.addEventListener('keydown', activateOnEnterOrSpace);
    }

    openDialog = { root: dialog, returnTo: document.activeElement };
    document.addEventListener('keydown', trapDialogKeys, true);

    // Focus the dialog itself, not its first button. $vs.dialog confirms put "Accept"
    // first, and the Enter key-up that opened the dialog would land on it and accept
    // without the user reading anything. The name and description are announced from
    // the container; Tab then reaches the buttons.
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  });
}

function releaseClosedDialog() {
  if (!openDialog || document.contains(openDialog.root)) return;
  document.removeEventListener('keydown', trapDialogKeys, true);
  const el = openDialog.returnTo;
  openDialog = null;
  if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
}

// ---------------------------------------------------------------------------------------
// 4. vs-list titles (audit 11.2, 11.4)
// ---------------------------------------------------------------------------------------

function enhanceListTitles(root) {
  selectAll(root, '.vs-list--header .vs-list--title').forEach((el) => {
    if (!el.hasAttribute('role')) {
      el.setAttribute('role', 'heading');
      el.setAttribute('aria-level', '2');
    }
  });
  selectAll(root, '.vs-list--item .vs-list--title').forEach((el) => {
    if (!el.hasAttribute('role')) {
      el.setAttribute('role', 'heading');
      el.setAttribute('aria-level', '3');
    }
  });
}

// ---------------------------------------------------------------------------------------
// 5. Answer marking (audit 17.1, 17.2)
// ---------------------------------------------------------------------------------------

const CONTROL = 'input:not([type="hidden"]), select, textarea';
const MARK_CLASS = 'a11y-answer-mark';

function makeMark(kind) {
  const mark = document.createElement('span');
  mark.className = `${MARK_CLASS} ${MARK_CLASS}--${kind}`;
  mark.setAttribute('role', 'img');
  mark.setAttribute('aria-label', kind === 'wrong' ? 'Incorrect answer' : 'Correct answer');
  mark.textContent = kind === 'wrong' ? '✖' : '✔';
  return mark;
}

function isControl(el) {
  return el.matches && el.matches(CONTROL);
}

function markedElement(el) {
  // The mark sits inside a container, or directly after a bare form control.
  if (isControl(el)) {
    const next = el.nextElementSibling;
    return next && next.classList.contains(MARK_CLASS) ? next : null;
  }
  return el.querySelector(`:scope > .${MARK_CLASS}`);
}

function syncAnswerMark(el) {
  const wrong = el.classList.contains('wrong');
  const correct = !wrong && el.classList.contains('correct');
  const want = wrong ? 'wrong' : correct ? 'correct' : null;

  const existing = markedElement(el);
  const have = existing
    ? existing.classList.contains(`${MARK_CLASS}--wrong`)
      ? 'wrong'
      : 'correct'
    : null;

  if (have === want) return;
  if (existing) existing.remove();

  const control = isControl(el) ? el : el.querySelector(CONTROL);
  if (control) {
    if (wrong) control.setAttribute('aria-invalid', 'true');
    else control.removeAttribute('aria-invalid');
  }

  if (!want) return;
  const mark = makeMark(want);
  if (isControl(el)) el.insertAdjacentElement('afterend', mark);
  else el.insertBefore(mark, el.firstChild);
}

function enhanceAnswerMarks(root) {
  selectAll(root, '.wrong, .correct').forEach(syncAnswerMark);
}

// ---------------------------------------------------------------------------------------
// 6. Unnamed form controls (audit 13.1, extrapolated)
// Activities render a question as a <p>/<h5> followed by a bare <select>, <textarea> or
// text <input> with no <label>. The visible question is the name a sighted user reads,
// so derive it from the nearest preceding text when nothing else names the control.
// ---------------------------------------------------------------------------------------

const NAMEABLE =
  'select, textarea, input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="submit"]):not([type="button"])';

function hasAccessibleName(el) {
  if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) return true;
  if (el.getAttribute('title')) return true;
  if (el.closest('label')) return true;
  if (el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`)) return true;
  return false;
}

function visibleTextOf(el) {
  if (!el || el.nodeType !== 1) return '';
  if (el.matches('button, a, input, select, textarea, script, style, .a11y-answer-mark')) return '';
  if (el.querySelector('input, select, textarea, button, table')) return '';
  if (el.getAttribute('aria-hidden') === 'true') return '';
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

// The closest preceding block of text, looking at earlier siblings first and then at the
// earlier siblings of each ancestor. Bounded so a stray control cannot pick up a heading
// from the other end of the page.
function precedingText(el) {
  let node = el;
  for (let depth = 0; node && node !== document.body && depth < 6; depth += 1) {
    for (let s = node.previousElementSibling; s; s = s.previousElementSibling) {
      const t = visibleTextOf(s);
      if (t) return t.length > 200 ? `${t.slice(0, 197)}...` : t;
    }
    node = node.parentElement;
  }
  return '';
}

function nameControls(root) {
  selectAll(root, NAMEABLE).forEach((el) => {
    if (hasAccessibleName(el)) return;
    const text = precedingText(el);
    if (text) el.setAttribute('aria-label', text);
  });
}

// ---------------------------------------------------------------------------------------

function enhance(root) {
  if (!root || root.nodeType !== 1) return;
  enhanceDialogs(root);
  enhanceCollapse(root);
  enhanceListTitles(root);
  enhanceAnswerMarks(root);
  hideFontIcons(root);
  // Deferred to a macrotask so that vuesaxFormA11y (which names vuesax controls from
  // their label-placeholder on nextTick) has run first; only controls still unnamed
  // then fall back to the preceding text.
  setTimeout(() => {
    if (document.contains(root)) nameControls(root);
  }, 0);
}

export default function installA11yDom(root = document.body) {
  if (!root || typeof MutationObserver === 'undefined') return null;

  enhance(root);

  const observer = new MutationObserver((mutations) => {
    let dialogRemoved = false;

    mutations.forEach((m) => {
      if (m.type === 'attributes') {
        const t = m.target;
        // A class flip on an answer container, or `open-item` on a collapse item.
        if (t.matches('.wrong, .correct') || markedElement(t)) syncAnswerMark(t);
        if (t.matches('.vs-collapse-item')) enhanceCollapse(t);
        return;
      }
      m.addedNodes.forEach(enhance);
      m.removedNodes.forEach((n) => {
        if (n.nodeType === 1 && (n.matches('.vs-dialog') || n.querySelector('.vs-dialog'))) {
          dialogRemoved = true;
        }
      });
    });

    if (dialogRemoved) releaseClosedDialog();
  });

  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  return observer;
}
